/* global
  createLoadMoreButton,
  waterdropAppApi
*/

/**
 * 积分抽奖组件 — 九宫格转盘抽奖
 * 包含：活动信息加载、奖品网格渲染、转盘动画、中奖弹窗、抽奖记录等功能
 */
class RewardsPrizeDraw extends HTMLElement {
  /** 转盘格子的顺序索引（顺时针） */
  static SPIN_ORDER = [0, 1, 2, 3, 4, 5, 6, 7]
  /** postDraw + 减速动画最短总时长（毫秒） */
  static MIN_DRAW_AND_ANIMATE_DURATION = 3000
  /** 预旋转每步间隔（毫秒） */
  static PRE_SPIN_STEP_INTERVAL = 80
  /** 折扣码状态文案（API 返回英文枚举，前端展示德语） */
  static DISCOUNT_STATUS_LABELS = {
    EXPIRED: 'ABGELAUFEN',
    ACTIVE: 'AKTIV',
    SCHEDULED: 'GEPLANT',
    USED: 'VERWENDET',
    DISABLE: 'DEAKTIVIERT'
  }

  constructor() {
    super()
    this.activity = null // 活动信息
    this.prizes = [] // 奖品列表
    this.customerInfo = null // 当前用户信息
    this.customerRecords = [] // 当前用户抽奖记录
    this.winnerRecords = [] // 中奖记录列表
    this.isSpinning = false // 是否正在旋转
    this._cellEls = [] // 奖品格子 DOM 缓存
    this._animFrame = null // 动画定时器句柄
    this._viewObserver = null // 抽奖板块曝光监听
    this._hasViewedEventSent = false // 避免重复上报曝光埋点
  }

  /** 是否已登录（通过 HTML 属性判断） */
  get isLoggedIn() {
    return this.hasAttribute('data-logged-in')
  }

  get activityId() {
    return this.dataset.activityId
  }

  get customerId() {
    return this.dataset.customerId || ''
  }

  getDiscountStatusLabel(status) {
    if (!status) return ''

    const normalizedStatus = String(status).toUpperCase()
    return RewardsPrizeDraw.DISCOUNT_STATUS_LABELS[normalizedStatus] || status
  }

  /** 组件挂载到 DOM 时触发，只初始化一次 */
  connectedCallback() {
    if (!this.activityId) {
      window.showMessage('Activity ID is required', 'error')
      return
    }
    if (this._initialized) return
    this._initialized = true
    this.cacheDOM()
    this.initViewTracking()
    this.load()
  }

  initViewTracking() {
    if (this._hasViewedEventSent || this._viewObserver) return

    if (!('IntersectionObserver' in window)) {
      this.sendViewedEvent()
      return
    }

    this._viewObserver = new IntersectionObserver(
      entries => {
        const hasReachedThreshold = entries.some(
          entry => entry.target === this && entry.isIntersecting && entry.intersectionRatio >= 0.5
        )

        if (!hasReachedThreshold) return
        this.sendViewedEvent()
      },
      { threshold: 0.5 }
    )

    this._viewObserver.observe(this)
  }

  sendViewedEvent() {
    if (this._hasViewedEventSent) return
    window.WDSendEvent('wd_luckydraw_viewed', { member_level: this.dataset.memberLevel || '-' })

    this._hasViewedEventSent = true

    if (this._viewObserver) {
      this._viewObserver.disconnect()
      this._viewObserver = null
    }
  }

  /** 缓存页面中需要频繁操作的 DOM 元素，并绑定事件 */
  cacheDOM() {
    this.spinBtn = this.querySelector('[data-spin-btn]')
    this.spinCostEl = this.querySelector('[data-spin-cost]')
    this.gridEl = this.querySelector('[data-prize-grid]')
    this.gridCellTpl = this.querySelector('[data-grid-cell-tpl]')
    this.winnerListEl = this.querySelector('[data-winner-list]')
    this.winnerRowTpl = this.querySelector('[data-winner-row-tpl]')
    this.winnerEmptyTpl = this.querySelector('[data-winner-empty-tpl]')
    this.rulesOpener = this.querySelector('[data-rules-opener]')
    this.costTextEl = this.querySelector('[data-cost-text]')

    this.rulesModal = document.getElementById('RPD-RulesModal')
    this.loginModal = document.getElementById('RPD-LoginModal')
    this.winModal = document.getElementById('RPD-WinModal')
    this.winRuleModal = document.getElementById('RPD-WinRuleModal')
    this.recordsModal = document.getElementById('RPD-RecordsModal')
    this.recordRuleModal = document.getElementById('RPD-RecordRuleModal')
    this.recordsOpener = this.querySelector('.rpd__lottery-records-link')
    this.recordsListEl = this.recordsModal?.querySelector('[data-records-list]')

    if (this.recordsListEl) this._recordsSkeletonHTML = this.recordsListEl.innerHTML

    if (this.spinBtn) {
      this.spinBtn.addEventListener('click', () => this.handleSpinClick())
    }
    if (this.recordsOpener) {
      this.recordsOpener.addEventListener('click', () => this.handleRecordsClick())
    }
    if (this.recordsModal) {
      this.recordsModal.addEventListener('click', e => this.handleRecordRuleClick(e))
    }

    this._boundOnPointsChanged = this.onPointsChanged.bind(this)
    this._boundRecordRuleEsc = this.handleRecordRuleEsc.bind(this)
    document.addEventListener('points-changed', this._boundOnPointsChanged)
    document.addEventListener('keydown', this._boundRecordRuleEsc, true)
  }

  /** 登录用户先加载个人信息，再并行加载活动信息和中奖记录 */
  async load() {
    if (this.isLoggedIn) await this.loadCustomer()
    await Promise.all([this.loadActivity(), this.loadRecords()])
    this.updateSpinBtnState()
  }

  /** 加载活动详情（核心接口，失败时给用户提示） */
  async loadActivity() {
    const res = await waterdropAppApi({ proxy: 'rewards/api', endpoint: `/activities/${this.activityId}` })
    if (res.code !== 200) {
      window.showMessage('Aktivität konnte nicht geladen werden. Bitte versuchen Sie es später erneut.', 'error')
      return
    }
    this.activity = res.data.activity
    this.prizes = res.data.prizes || []
    this.renderGrid()
    this.renderActivityInfo()
  }

  /** 加载中奖记录（非核心，失败只打日志） */
  async loadRecords() {
    const res = await waterdropAppApi({
      proxy: 'rewards/api',
      endpoint: '/activities/records',
      query: { id: this.activityId, limit: 100 }
    })
    if (!res?.data?.list) return
    this.winnerRecords = res.data.list
    this.renderWinnerList()
  }

  /** 加载登录用户的活动个人信息（非核心，失败只打日志） */
  async loadCustomer() {
    const res = await waterdropAppApi({
      proxy: 'rewards/api',
      endpoint: '/customer',
      query: { activity_id: this.activityId }
    })
    if (!res?.data?.customer_info) return
    this.customerInfo = res.data.customer_info

    const checkInEl = document.querySelector('rewards-check-in')
    if (checkInEl) checkInEl.initCheckedIn(this.customerInfo.checked_in)
    // 更新页面上的积分值
    document.querySelectorAll('[data-points-value]').forEach(el => {
      el.textContent = this.customerInfo.points
    })
  }

  /** 发起抽奖请求，返回中奖结果 */
  async postDraw() {
    const res = await waterdropAppApi({
      proxy: 'rewards/api',
      endpoint: '/draws',
      method: 'POST',
      payload: { id: this.activityId }
    })
    return res
  }

  /** 点击 Lottery Records 时重新拉取最新数据并渲染弹窗内容 */
  async handleRecordsClick() {
    if (!this.isLoggedIn) return

    // 重置分页状态
    this.customerRecords = []
    this.recordsPage = 0
    this.recordsHasMore = false
    this.recordsLoadingMore = false

    // 恢复骨架屏，让用户感知到正在加载
    if (this.recordsListEl && this._recordsSkeletonHTML) {
      this.recordsListEl.innerHTML = this._recordsSkeletonHTML
    }
    this.recordsModal?.querySelector('.rpd__records-load-more')?.remove()

    try {
      await this.loadRecordsPage(1)
    } catch (e) {
      console.error('[RewardsPrizeDraw] load records error:', e)
      if (typeof window.showMessage === 'function') {
        window.showMessage('Einträge konnten nicht geladen werden. Bitte versuchen Sie es später erneut.', 'error')
      }
      this.renderRecordsModal()
    }
  }

  /** 请求指定页的抽奖记录并合并到列表 */
  async loadRecordsPage(page) {
    const recordsRes = await waterdropAppApi({
      proxy: 'rewards/api',
      endpoint: '/customer/draw_records',
      query: { page, limit: 50 }
    })
    const list = recordsRes?.data?.list || []
    const hasMore = recordsRes?.data?.has_more

    if (page <= 1) {
      this.customerRecords = list
    } else {
      this.customerRecords = (this.customerRecords || []).concat(list)
    }
    this.recordsPage = page
    this.recordsHasMore = hasMore
    this.renderRecordsModal()
  }

  /** 点击 View More：加载下一页记录并追加到列表末尾 */
  async handleLoadMoreRecords() {
    if (this.recordsLoadingMore || !this.recordsHasMore) return
    this.recordsLoadingMore = true
    try {
      await this.loadRecordsPage((this.recordsPage || 1) + 1)
    } catch (e) {
      console.error('[RewardsPrizeDraw] load more records error:', e)
      window.showMessage?.(
        'Weitere Einträge konnten nicht geladen werden. Bitte versuchen Sie es später erneut.',
        'error'
      )
      throw e
    } finally {
      this.recordsLoadingMore = false
    }
  }

  /* ---- 渲染：九宫格奖品网格 ---- */

  /** 根据奖品列表生成九宫格（中间格为抽奖按钮） */
  renderGrid() {
    if (!this.gridEl || !this.prizes.length) return

    const spinCell = this.gridEl.querySelector('.rpd__grid-cell--spin')
    const spinCellDetached = spinCell ? spinCell.parentNode.removeChild(spinCell) : null

    this.gridEl.innerHTML = ''
    this._cellEls = []

    // 九宫格布局映射：-1 表示中间抽奖按钮位置
    const gridOrder = [0, 1, 2, 7, -1, 3, 6, 5, 4]

    gridOrder.forEach(idx => {
      if (idx === -1) {
        if (spinCellDetached) this.gridEl.appendChild(spinCellDetached)
        return
      }

      const prize = this.prizes[idx]
      if (!prize) return

      const clone = this.gridCellTpl.content.cloneNode(true)
      const cell = clone.querySelector('.rpd__grid-cell')
      cell.dataset.prizeIndex = idx

      const img = cell.querySelector('.rpd__prize-img')
      img.src = prize.prize_image || ''
      img.alt = prize.prize_name || ''

      cell.querySelector('.rpd__prize-name').textContent = prize.prize_name || ''

      this.gridEl.appendChild(clone)
      this._cellEls[idx] = cell
    })
  }

  /* ---- 渲染：活动信息（消耗积分、规则等） ---- */
  renderActivityInfo() {
    if (!this.activity) return

    const cost = this.activity.consumption_points || 0

    if (this.costTextEl) {
      this.costTextEl.textContent = `${cost}`
    }
    this.renderSpinCostText()

    if (this.rulesOpener) {
      if (this.activity.rules_description) {
        this.rulesOpener.style.display = 'block'
        const rulesBody = this.rulesModal?.querySelector('.rpd__rules-body')
        if (rulesBody) rulesBody.innerHTML = this.activity.rules_description
      } else {
        this.rulesOpener.style.display = 'none'
      }
    }
  }

  renderSpinCostText() {
    if (!this.spinCostEl || !this.activity) return

    const cost = this.activity.consumption_points || 0
    const dailyDrawLimit = this.activity.daily_draw_limit || 0
    const customerRemainingFreeDraws = this.customerInfo?.remaining_free_draws || 0
    const customerRemainingDraw = this.customerInfo?.remaining_draw
    if (this.isLoggedIn) {
      let spinText = ''
      if (customerRemainingFreeDraws > 0) {
        spinText = `Sie haben ${customerRemainingFreeDraws} kostenlose Drehungen!`
      } else if (customerRemainingDraw === 0) {
        spinText = 'Keine Drehungen mehr verfügbar'
      } else {
        spinText = `${cost} Punkte pro Drehung`
      }
      this.spinCostEl.textContent = spinText
      return
    }

    this.spinCostEl.textContent =
      dailyDrawLimit > 0 ? `Sie haben ${dailyDrawLimit} kostenlose Drehungen!` : `${cost} Punkte pro Drehung`
  }

  /* ---- 渲染：中奖滚动列表 ---- */

  /** 渲染全站中奖记录列表 */
  renderWinnerList() {
    if (!this.winnerListEl) return

    if (!this.winnerRecords.length) {
      this.winnerListEl.replaceChildren(this.winnerEmptyTpl.content.cloneNode(true))
      this.winnerListEl.classList.remove('rpd__winner-list--scrollable')
      return
    }

    const frag = document.createDocumentFragment()
    this.winnerRecords.forEach(rec => {
      const row = this.winnerRowTpl.content.cloneNode(true)
      row.querySelector('.rpd__winner-email').textContent = rec.email || ''
      row.querySelector('.rpd__winner-prize').textContent = `Gewonnen ${rec.prize_name || ''}`
      frag.appendChild(row)
    })

    const inner = document.createElement('div')
    inner.className = 'rpd__winner-scroll-inner rpd__winner-scroll-inner--static'
    inner.appendChild(frag)

    this.winnerListEl.innerHTML = ''
    this.winnerListEl.appendChild(inner)

    requestAnimationFrame(() => this._setupWinnerScroll(inner))
  }

  /** 检测内容溢出并启动无限滚动 */
  _setupWinnerScroll(inner) {
    const container = this.winnerListEl
    const contentHeight = inner.scrollHeight
    const containerHeight = container.clientHeight

    if (contentHeight <= containerHeight) {
      inner.classList.add('rpd__winner-scroll-inner--static')
      container.classList.remove('rpd__winner-list--scrollable')
      return
    }

    container.classList.add('rpd__winner-list--scrollable')

    const clone = inner.cloneNode(true)
    clone.className = 'rpd__winner-scroll-clone'
    clone.removeAttribute('style')
    inner.appendChild(clone)

    const speed = 30
    const duration = contentHeight / speed
    inner.style.setProperty('--rpd-scroll-duration', `${duration}s`)
    inner.classList.remove('rpd__winner-scroll-inner--static')
  }

  /** 将本次中奖记录插到 winnerRecords 最前面并重新渲染列表 */
  prependWinnerRecord(data) {
    if (!data || !data.prize_name || data.prize_type === 'NO_PRIZE') return
    const email = this.customerInfo?.email || ''
    const masked = email.replace(/^(.).*(@.*)$/, '$1***$2')
    this.winnerRecords.unshift({
      id: data.draw_id,
      email: masked,
      prize_name: data.prize_name,
      win_time: new Date().toISOString()
    })
    this.renderWinnerList()
  }

  /* ---- 抽奖按钮状态管理 ---- */

  /** 根据活动状态、登录状态、免费次数、积分余额来决定按钮是否可点击 */
  updateSpinBtnState() {
    if (!this.spinBtn) return

    if (!this.activity || this.activity.status !== 'PROGRESS') {
      this.spinBtn.disabled = true
      this.spinCostEl.textContent = this.activity?.status === 'PENDING' ? 'Noch nicht gestartet' : 'Beendet'
      return
    }

    if (!this.isLoggedIn) {
      this.spinBtn.disabled = false
      return
    }

    if (!this.customerInfo) {
      this.spinBtn.disabled = true
      return
    }

    const remainingFreeDraws = this.customerInfo.remaining_free_draws || 0
    const remainingDraw = this.customerInfo.remaining_draw
    const cost = this.activity.consumption_points || 0
    const hasFreeDraws = remainingFreeDraws > 0
    const hasPaidDraws = remainingDraw === undefined || remainingDraw === null || remainingDraw > 0
    const hasEnoughPoints = this.customerInfo.points >= cost

    if (hasFreeDraws) {
      this.spinBtn.disabled = false
      return
    }

    if (hasPaidDraws && hasEnoughPoints) {
      this.spinBtn.disabled = false
      return
    }

    this.spinBtn.disabled = true
    if (!hasEnoughPoints) {
      this.spinCostEl.textContent = 'Nicht genügend Punkte'
    }
  }

  /** 外部积分变动时同步本地状态并刷新按钮 */
  onPointsChanged(e) {
    const newPoints = e.detail?.points
    if (newPoints == null || !this.customerInfo) return
    this.customerInfo.points = newPoints
    this.updateSpinBtnState()
    this.renderSpinCostText()
  }

  /* ---- 抽奖核心流程 ---- */

  /**
   * 抽奖按钮主流程，分四个阶段：
   *   Phase 1 — 前置校验（防重复点击、登录状态、按钮禁用）
   *   Phase 2 — 并行执行：发起抽奖请求 + 启动匀速预旋转动画
   *   Phase 3 — 请求返回后，减速旋转停到中奖格子
   *   Phase 4 — 同步用户数据（积分/剩余次数），弹出中奖弹窗
   */
  async handleSpinClick() {
    // ── Phase 1: 前置校验 ──
    if (this.isSpinning || !this.isLoggedIn) {
      if (!this.isLoggedIn) this.showLoginModal()
      return
    }
    if (this.spinBtn?.disabled) return

    window.WDSendEvent('wd_luckydraw_clicked', { member_level: this.dataset.memberLevel || '-' })
    this.isSpinning = true
    this.spinBtn.disabled = true

    // ── Phase 2: 同时发起抽奖请求 & 播放预旋转动画 ──
    const drawStartAt = Date.now()
    const drawPromise = this.postDraw()
    this.startPreSpinAnimation()
    let drawResult
    try {
      const res = await drawPromise
      if (res.code && res.code !== 200) throw res
      drawResult = res
      window.WDSendEvent('wd_luckydraw_completed', {
        event_category: res.data.prize_type,
        member_level: this.dataset.memberLevel || '-',
        event_label: res.data.today_draw_count,
        value: res.data.prize_name
      })
    } catch (err) {
      this.resetSpinState()
      const msg = err.message || 'Ziehung fehlgeschlagen. Bitte versuchen Sie es erneut.'
      if (typeof window.showMessage === 'function') window.showMessage(msg, 'error')
      return
    }

    // ── Phase 3: 减速动画 — 停到目标格子 ──
    const targetIndex = this.findWinnerIndex(drawResult.data)
    // 保证"请求耗时 + 动画耗时"不低于 MIN_DRAW_AND_ANIMATE_DURATION，体感更流畅
    const minAnimateDuration = Math.max(0, RewardsPrizeDraw.MIN_DRAW_AND_ANIMATE_DURATION - (Date.now() - drawStartAt))

    const preSpinStep = this._preSpinStep || 0
    this.stopPreSpinAnimation()
    await this.animateSpinToTarget(targetIndex, minAnimateDuration, preSpinStep)

    // ── Phase 4: 同步用户数据 & 展示结果 ──
    this.syncCustomerInfo(drawResult.data)
    this.prependWinnerRecord(drawResult.data)
    this.isSpinning = false
    this.updateSpinBtnState()
    this.showWinModal(drawResult.data)
  }

  /** 抽奖出错或中断时，统一恢复旋转前的 UI 状态 */
  resetSpinState() {
    this.stopPreSpinAnimation()
    if (this.gridEl) this.gridEl.classList.remove('rpd__grid--spinning')
    this.isSpinning = false
    this.updateSpinBtnState()
  }

  /** 用抽奖返回的最新字段合并到本地 customerInfo，避免额外请求 */
  syncCustomerInfo(data) {
    if (!data) return
    this.customerInfo = {
      ...this.customerInfo,
      points: data.points,
      remaining_free_draws: data.remaining_free_draws,
      remaining_draw: data.remaining_draw
    }

    // 同步页面所有的积分值
    document.querySelectorAll('[data-points-value]').forEach(el => {
      el.textContent = this.customerInfo.points.toLocaleString() ?? '--'
    })
    this.renderSpinCostText()
  }

  /** 根据抽奖结果找到对应的奖品格子索引 */
  findWinnerIndex(drawData) {
    if (!drawData || !this.prizes.length) return 0

    const idx = this.prizes.findIndex(p => String(p.prize_id) === String(drawData.prize_id))
    return idx >= 0 ? idx : 0
  }

  /* ---- 转盘动画：第一阶段 — 匀速快速循环 ---- */

  /**
   * 匀速预旋转 — 在等待 postDraw 接口返回期间持续播放。
   *
   * 使用"时间补偿"：每帧根据实际经过时间计算应推进的步数，
   * 保证在浏览器节流（后台标签页等）恢复后不丢步。
   *
   * 结束后 this._preSpinStep 会被 handleSpinClick 传给 animateSpinToTarget，
   * 使减速动画能从当前高亮位置无缝衔接。
   */
  startPreSpinAnimation() {
    this._preSpinRunning = true
    this._preSpinStep = 0
    this._preSpinPrevCell = null
    this._preSpinLastTickAt = performance.now()
    if (this.gridEl) this.gridEl.classList.add('rpd__grid--spinning')

    const order = RewardsPrizeDraw.SPIN_ORDER
    const totalCells = order.length
    const interval = RewardsPrizeDraw.PRE_SPIN_STEP_INTERVAL

    const activateNextCell = () => {
      if (this._preSpinPrevCell) this._preSpinPrevCell.classList.remove('rpd__grid-cell--active')

      const cell = this._cellEls[order[this._preSpinStep % totalCells]]
      if (cell) {
        cell.classList.add('rpd__grid-cell--active')
        this._preSpinPrevCell = cell
      }
      this._preSpinStep++
    }

    const tick = () => {
      if (!this._preSpinRunning) return

      // 根据真实流逝时间一次性推进 N 步，抹平帧率抖动
      const elapsed = performance.now() - this._preSpinLastTickAt
      const stepsToAdvance = Math.floor(elapsed / interval)

      if (stepsToAdvance >= 1) {
        for (let i = 0; i < stepsToAdvance; i++) activateNextCell()
        this._preSpinLastTickAt += stepsToAdvance * interval
      }

      this._animFrame = setTimeout(tick, 16)
    }

    this._animFrame = setTimeout(tick, 16)
  }

  /** 停止预旋转动画 */
  stopPreSpinAnimation() {
    this._preSpinRunning = false
    if (this._animFrame) {
      clearTimeout(this._animFrame)
      this._animFrame = null
    }
    if (this._preSpinPrevCell) {
      this._preSpinPrevCell.classList.remove('rpd__grid-cell--active')
      this._preSpinPrevCell = null
    }
  }

  /* ---- 转盘动画：第二阶段 — 减速停到目标格子 ---- */

  /**
   * 减速旋转动画 — 从 startStep（预旋转结束位置）开始，
   * 经过 N 圈完整循环后精确停在 targetIndex 对应的格子。
   *
   * @param {number} targetIndex    中奖奖品在 this.prizes 中的索引
   * @param {number} minDurationMs  动画最短持续时间（补偿接口耗时，保证体感时长）
   * @param {number} startStep      预旋转已走的步数，用于无缝衔接高亮位置
   */
  animateSpinToTarget(targetIndex, minDurationMs = 0, startStep = 0) {
    return new Promise(resolve => {
      const order = RewardsPrizeDraw.SPIN_ORDER
      const totalCells = order.length
      const targetOrderIdx = Math.max(0, order.indexOf(targetIndex))
      const safeStartStep = Number.isFinite(startStep) ? Math.max(0, Math.floor(startStep)) : 0
      // startStep 是“下一次要点亮的位置”，所以“当前已点亮位置” = startStep - 1
      const currentOrderIdx = (safeStartStep - 1 + totalCells) % totalCells

      let fullLoops = 2
      // ── 计算总步数：至少转 2 整圈，且总时长 >= minDurationMs ──
      const stepsToTarget = (targetOrderIdx - currentOrderIdx + totalCells) % totalCells
      let totalSteps = fullLoops * totalCells + stepsToTarget
      while (this._estimateSpinDuration(totalSteps) < minDurationMs && fullLoops < 20) {
        fullLoops++
        totalSteps = fullLoops * totalCells + stepsToTarget
      }

      // ── 逐帧推进 ──
      let step = safeStartStep
      let progressedSteps = 0
      let prevCell = null

      const tick = () => {
        if (prevCell) prevCell.classList.remove('rpd__grid-cell--active')

        const cell = this._cellEls[order[step % totalCells]]

        if (cell) {
          cell.classList.add('rpd__grid-cell--active')
          prevCell = cell
        }

        progressedSteps++
        step++

        // 到达目标格 → 切换为 winner 高亮，短暂停留后 resolve
        if (progressedSteps >= totalSteps) {
          if (cell) {
            cell.classList.remove('rpd__grid-cell--active')
            cell.classList.add('rpd__grid-cell--winner')
          }
          setTimeout(() => {
            if (cell) cell.classList.remove('rpd__grid-cell--winner')
            if (this.gridEl) this.gridEl.classList.remove('rpd__grid--spinning')
            resolve()
          }, 600)
          return
        }

        this._animFrame = setTimeout(tick, this._getSpinDelay(progressedSteps, totalSteps))
      }

      tick()
    })
  }

  /**
   * 三段式减速延迟曲线：
   *   0%~40%   匀速快转  70ms/步
   *   40%~70%  线性减速  70→220ms/步
   *   70%~100% 深度减速  220→490ms/步，营造"即将停下"的紧张感
   */
  _getSpinDelay(currentStep, totalSteps) {
    const progress = currentStep / totalSteps
    if (progress < 0.4) return 70
    if (progress < 0.7) return 70 + (progress - 0.4) * 500
    return 220 + (progress - 0.7) * 900
  }

  /** 预估给定步数下的总动画时长（含 600ms winner 停留） */
  _estimateSpinDuration(steps) {
    let duration = 600
    for (let s = 1; s < steps; s++) duration += this._getSpinDelay(s, steps)
    return duration
  }

  /* ---- 弹窗相关 ---- */

  /** 显示登录弹窗 */
  showLoginModal() {
    if (this.loginModal && typeof this.loginModal.show === 'function') {
      this.loginModal.show()
    }
  }

  /** 显示中奖弹窗：展示奖品图片、名称、折扣码、CTA 按钮等 */
  showWinModal(data) {
    if (!this.winModal || !data) return

    const img = this.winModal.querySelector('[data-win-image]')
    const title = this.winModal.querySelector('[data-win-title]')
    const message = this.winModal.querySelector('[data-win-message]')
    const codeWrap = this.winModal.querySelector('[data-win-code-wrap]')
    const ruleEl = this.winModal.querySelector('[data-win-rule]')
    const ctaLink = this.winModal.querySelector('[data-win-cta-link]')
    const ctaBtn = this.winModal.querySelector('[data-win-btn]')
    const closeBtn = this.winModal.querySelector('[data-win-close]')

    if (img) {
      img.src = data.prize_image || ''
      img.alt = data.prize_name || ''
    }
    if (title) title.textContent = data.prize_name || ''
    if (message) message.textContent = data.winning_pop_up_message || ''

    const code = data.prize_type === 'NO_PRIZE' ? '' : data.discount_code || data.gift_card_code || ''
    if (codeWrap) {
      const codeBtn = codeWrap.querySelector('.component-code-button')
      if (code) {
        codeWrap.style.display = ''
        if (codeBtn) {
          codeBtn.style.display = ''
          codeBtn.dataset.value = code
          const codeText = codeBtn.querySelector('p')
          if (codeText) codeText.textContent = code
        }
      } else if (this.hasRichTextContent(data.winning_pop_up_rule)) {
        codeWrap.style.display = ''
        codeBtn.style.display = 'none'
      } else {
        codeWrap.style.display = 'none'
      }
    }

    if (ruleEl) {
      if (this.hasRichTextContent(data.winning_pop_up_rule)) {
        ruleEl.style.display = ''
        const body = this.winRuleModal?.querySelector('[data-win-rule-body]')
        if (body) body.innerHTML = data.winning_pop_up_rule
      } else {
        ruleEl.style.display = 'none'
      }
    }

    if (ctaLink) ctaLink.classList.add('hidden')
    if (ctaBtn) ctaBtn.classList.add('hidden')

    const ctaText = data.winning_pop_up_cta_text || 'Play again'
    const ctaUrl = typeof data.winning_pop_up_cta_url === 'string' ? data.winning_pop_up_cta_url.trim() : ''
    if (ctaUrl && ctaLink) {
      ctaLink.classList.remove('hidden')
      ctaLink.textContent = ctaText
      ctaLink.href = ctaUrl
    } else if (ctaBtn) {
      ctaBtn.classList.remove('hidden')
      ctaBtn.textContent = ctaText
      ctaBtn.onclick = () => {
        this.winModal.hide()
        if (data.prize_type === 'POINTS') {
          window.SmileUI.openPanel({ deep_link: 'points_products' })
        }
      }
    }

    if (closeBtn) {
      closeBtn.onclick = () => this.winModal.hide()
    }

    this.winModal.show()
  }

  /** 显示个人抽奖记录弹窗：包含奖品卡片、折扣码复制、状态标签等 */
  renderRecordsModal() {
    if (!this.recordsModal) return

    const listEl = this.recordsListEl || this.recordsModal.querySelector('[data-records-list]')
    const body = this.recordsModal.querySelector('.rpd__records-body')
    const tpl = document.getElementById('RPD-RecordCardTpl')
    if (!listEl || !body || !tpl) return

    body.querySelector('.rpd__records-load-more')?.remove()

    const records = this.customerRecords || []
    if (!records.length) {
      const emptyTpl = this.recordsModal.querySelector('[data-records-empty-tpl]')
      listEl.innerHTML = ''
      if (emptyTpl) listEl.appendChild(emptyTpl.content.cloneNode(true))
      return
    }

    const frag = document.createDocumentFragment()
    const REWARD_TYPES = ['PRODUCT_DISCOUNT_CODE', 'ORDER_DISCOUNT_CODE', 'GIFT_CARD', 'CUSTOM']
    const BADGE_CLS_MAP = { active: 'rpd__record-badge--active', expired: 'rpd__record-badge--expired' }

    records.forEach((rec, index) => {
      const card = tpl.content.cloneNode(true).firstElementChild

      card.querySelector('[data-tpl-prize-name]').textContent = rec.prize_name || ''
      card.querySelector('[data-tpl-draw-time]').textContent = rec.draw_time ? this.formatDateTime(rec.draw_time) : '--'
      let pointsText = '--'
      if (rec.consumption_points) pointsText = `-${rec.consumption_points} Punkte`
      else if (rec.used_free_draws) pointsText = 'Kostenlos'
      card.querySelector('[data-tpl-points-spent]').textContent = pointsText

      const isReward = REWARD_TYPES.includes(rec.prize_type)
      const isCustom = rec.prize_type === 'CUSTOM'
      const showCode = isReward && !isCustom && !!rec.discount_code
      const badge = card.querySelector('[data-tpl-badge]')
      if (isReward && rec.discount_status) {
        const statusLower = rec.discount_status.toLowerCase()
        badge.className = `rpd__record-badge ${BADGE_CLS_MAP[statusLower] || 'rpd__record-badge--pending'}`
        badge.textContent = this.getDiscountStatusLabel(rec.discount_status)
        badge.style.display = ''
      }

      const codeSection = card.querySelector('[data-tpl-code-section]')
      if (showCode || isCustom) {
        codeSection.style.display = ''
        if (showCode) {
          const codeBtn = codeSection.querySelector('.component-code-button')
          if (codeBtn) {
            codeBtn.dataset.value = rec.discount_code
            const codeText = codeBtn.querySelector('p')
            if (codeText) codeText.textContent = rec.discount_code
          }
        } else {
          const codeEl = codeSection.querySelector('.rpd__record-code')
          const divider = codeSection.querySelector('.rpd__record-meta-divider')
          if (codeEl) codeEl.style.display = 'none'
          if (divider) divider.style.display = 'none'
        }

        const validity = codeSection.querySelector('[data-tpl-validity]')
        if (validity) {
          validity.style.display = ''
          const createdDate = rec.discount_code_start
            ? this.formatDateDay(rec.discount_code_start)
            : this.formatDateDay(rec.draw_time)
          const expiresDate = rec.discount_code_expiry ? this.formatDateDay(rec.discount_code_expiry) : null
          const datesText = expiresDate ? `${createdDate}-${expiresDate}` : 'Keine zeitliche Begrenzung'
          validity.querySelector('[data-tpl-validity-value]').textContent = datesText
        }
      } else {
        card.classList.add('rpd__record-row--compact')
      }

      const ruleBtn = card.querySelector('[data-tpl-rules]')
      if (ruleBtn && this.hasRichTextContent(rec.winning_pop_up_rule)) {
        ruleBtn.dataset.recordIndex = String(index)
        ruleBtn.style.display = ''
      }

      frag.appendChild(card)
    })

    listEl.innerHTML = ''
    listEl.appendChild(frag)

    if (this.recordsHasMore) {
      createLoadMoreButton({
        containerEl: body,
        wrapClass: 'rpd__records-load-more',
        variant: 'text',
        onClick: () => this.handleLoadMoreRecords()
      })
    }
  }

  handleRecordRuleClick(event) {
    const btn = event.target.closest('[data-tpl-rules]')
    if (!btn || !this.recordsModal.contains(btn) || btn.style.display === 'none') return
    const rec = this.customerRecords?.[Number(btn.dataset.recordIndex)]
    this.showRecordRuleModal(rec)
  }

  handleRecordRuleEsc(event) {
    if (event.key !== 'Escape' && event.key !== 'Esc') return
    if (!this.recordRuleModal?.hasAttribute('open')) return
    event.stopImmediatePropagation()
    this.recordRuleModal.hide()
  }

  showRecordRuleModal(rec) {
    if (!this.recordRuleModal) return
    const body = this.recordRuleModal.querySelector('[data-record-rule-body]')
    if (body) body.innerHTML = rec.winning_pop_up_rule || ''
    this.recordRuleModal.show()
  }

  /* ---- 工具方法 ---- */

  /** 富文本去掉标签后是否还有可见文字。空段落如 <p><br></p> 视为无内容。 */
  hasRichTextContent(html) {
    if (typeof html !== 'string') return false
    const text = html
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;|&#160;|&#xa0;/gi, ' ')
      .replace(/\u00a0/g, ' ')
      .trim()
    return text.length > 0
  }

  /** 将 ISO 时间字符串格式化为 DD/MM/YYYY HH:mm */
  formatDateTime(isoStr) {
    try {
      const d = new Date(isoStr)
      const day = String(d.getDate()).padStart(2, '0')
      const mon = String(d.getMonth() + 1).padStart(2, '0')
      const year = d.getFullYear()
      const hour = String(d.getHours()).padStart(2, '0')
      const min = String(d.getMinutes()).padStart(2, '0')
      return `${day}.${mon}.${year} ${hour}:${min}`
    } catch {
      return isoStr
    }
  }

  /** 将 ISO 时间字符串格式化为 DD/MM/YYYY  */
  formatDateDay(isoStr) {
    try {
      const d = new Date(isoStr)
      const day = String(d.getDate()).padStart(2, '0')
      const mon = String(d.getMonth() + 1).padStart(2, '0')
      const year = d.getFullYear()
      return `${day}.${mon}.${year}`
    } catch {
      return isoStr
    }
  }

  disconnectedCallback() {
    if (this._animFrame) {
      clearTimeout(this._animFrame)
      this._animFrame = null
    }
    if (this._viewObserver) {
      this._viewObserver.disconnect()
      this._viewObserver = null
    }
    if (this._boundOnPointsChanged) {
      document.removeEventListener('points-changed', this._boundOnPointsChanged)
    }
    if (this._boundRecordRuleEsc) {
      document.removeEventListener('keydown', this._boundRecordRuleEsc, true)
    }
  }
}

if (!customElements.get('rewards-prize-draw')) {
  customElements.define('rewards-prize-draw', RewardsPrizeDraw)
}
